// validationUtils.ts - Funktionen für die Validierung von Trampolin-Bewegungen
import { normalizeInput } from './normalizeUtils';

/**
 * Validiert eine einzelne Trampolin-Bewegung
 */
export const validateMove = (
  move: string, 
  moveIndex: number, 
  exerciseIndex: number, 
  exercises: string[][]
): boolean => {
  if (!move) return true; // Empty field is valid
  
  // Use our custom normalize function to handle 'o'/'O' to '°' conversion
  const normalizedMove = normalizeInput(move);
  
  // Basic pattern validation - first check if this is a complete move
  if (normalizedMove.length < 2) {
    //console.log(`Move too short (need at least number + position): ${normalizedMove}`);
    return false;
  }
  
  // Extract parts for better analysis
  const numericPart = normalizedMove.match(/^[0-9]+/)?.[0] || "";
  const rest = normalizedMove.substring(numericPart.length);
  
  // Check the numeric part
  const validNumericPattern = /^(00|0[1-9]|[1-6][0-9]|[7-9][0-9]{2}|10[0-9]{2}|1[1-4][0-9]{3}|1[5-8][0-9]{4})$/;
  if (!validNumericPattern.test(numericPart)) {
    //console.log(`Invalid numeric part: ${numericPart} in move: ${normalizedMove}`);
    return false;
  }

  // Special case: Numbers 42-49 can be used without position character
  const isSpecialNumber = /^4[2-9]$/.test(numericPart);
  
  // Check the position and landing - we support /, <, °, V
  // Note: o and O are normalized to ° before this check
  // Note: \ is normalized to / before this check
  const validPositionLandingPattern = /^[\/\<°V][BRS]?$/;
  /*if (!validPositionLandingPattern.test(rest)) {
    //console.log(`Invalid position/landing: ${rest} in move: ${normalizedMove}`);
    return false;
  }*/

  // For special numbers (42-49), empty rest is also valid
  if (isSpecialNumber && rest === "") {
    // Numbers 42-49 without position character are valid
    return true;
  } else if (!validPositionLandingPattern.test(rest)) {
    //console.log(`Invalid position/landing: ${rest} in move: ${normalizedMove}`);
    return false;
  }
  
  // Special rules
  // 1. Position 'v' can only appear in '00v'
  if (normalizedMove.includes('V') && !normalizedMove.startsWith('00')) {
    //console.log(`Invalid 'v' position in non-00 move: ${normalizedMove}`);
    return false;
  }
  
  // 2. If first digit is 0 and second digit is not 0, only position '/' is allowed
  if (normalizedMove[0] === '0' && normalizedMove[1] !== '0' && !normalizedMove.includes('/')) {
    //console.log(`Invalid position for 0X move (must be /): ${normalizedMove}`);
    return false;
  }
  
  // 3. '00/' can only follow a jump with landing type 'S'
  if (normalizedMove === '00/' && moveIndex > 0) {
    const prevMove = exercises[exerciseIndex][moveIndex - 1];
    if (prevMove && !normalizeInput(prevMove).endsWith('S')) {
      //console.log(`00/ can only follow landing type S, previous move: ${prevMove}`);
      return false;
    }
  }
  
  // 4. Field 10 cannot have landing type B, R, or S
  if (moveIndex === 9 && /[BRS]$/.test(normalizedMove)) {
    //console.log(`Last move cannot have landing type B, R, or S: ${normalizedMove}`);
    return false;
  }
  
  return true;
};

/**
 * Prüft auf doppelte Bewegungen innerhalb einer Übung
 */
export const checkDuplicates = (exerciseIndex: number, exercises: string[][]): boolean[] => {
  const exercise = exercises[exerciseIndex];
  const newDuplicates = Array(10).fill(false);
  
  const normalizedMoves = exercise.map(move => 
    move ? normalizeInput(move) : ''
  );
  
  for (let i = 0; i < normalizedMoves.length; i++) {
    if (!normalizedMoves[i]) continue;
    
    for (let j = 0; j < i; j++) {
      if (normalizedMoves[i] === normalizedMoves[j]) {
        newDuplicates[i] = true;
        break;
      }
    }
  }
  
  return newDuplicates;
};

/**
 * Helper function to count moves with rotation less than 270°
 */
export const countMovesWithLessRotation = (moves: string[]): number => {
  return moves.filter(move => getRotationValue(move) < 3).length;
};

/**
 * Helper function to extract rotation value from move
 * This is a simplified implementation - you would need more complex parsing
 * based on your actual move format and rules
 */
export const getRotationValue = (move: string): number => {
  const normalizedMove = normalizeInput(move);
  const numericPart = normalizedMove.match(/^[0-9]+/)?.[0] || "";
  
  // Example logic (needs to be adjusted based on actual requirements):
  // Checking the first digit which often represents full rotations
  const firstDigit = parseInt(numericPart[0] || "0");
  return firstDigit;
};

/**
 * Helper function to extract twist value from move
 * This is a simplified implementation - you would need more complex parsing
 */
export const getTwistValue = (move: string): number => {
  const normalizedMove = normalizeInput(move);
  const numericPart = normalizedMove.match(/^[0-9]+/)?.[0] || "";
  
  // Example logic (needs to be adjusted based on actual requirements):
  // Checking the second digit which might represent twists
  const secondDigit = parseInt(numericPart[1] || "0");
  return secondDigit;
};